/**
 * The builder's Share link panel: AgencyAnalytics' Share Link, with Miso's
 * stricter options. A link opens a frozen copy (edit it with "Edit copy"
 * without touching this one), uses this report's period unless viewers may
 * change it, and can expire, need a password, or be revoked at any time.
 */

import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';

import type { RangeSetting } from './reports-api.ts';
import { sharesApi, type ShareLink } from './shares-api.ts';

const SHARE_PASSWORD_MIN = 8;
const when = new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium' });

function status(link: ShareLink): string {
  if (link.revoked_at) return 'Revoked';
  if (link.expires_at && new Date(link.expires_at) <= new Date()) return 'Expired';
  return link.expires_at ? `Live until ${when.format(new Date(link.expires_at))}` : 'Live';
}

function NewLinkForm(props: {
  defaultName: string;
  onCreate: (input: {
    name: string;
    allowRangeChange: boolean;
    expiresOn: string;
    password: string;
  }) => Promise<void>;
}): ReactNode {
  const [name, setName] = useState(props.defaultName);
  const [allowRangeChange, setAllowRangeChange] = useState(false);
  const [expiresOn, setExpiresOn] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await props.onCreate({ name: name.trim(), allowRangeChange, expiresOn, password });
      setPassword('');
    } finally {
      setBusy(false);
    }
  };
  const tooShort = password.length > 0 && password.length < SHARE_PASSWORD_MIN;
  return (
    <form className="rth" onSubmit={(event) => void submit(event)}>
      <label className="rs-field">
        Link name (only your team sees it)
        <input
          value={name}
          maxLength={160}
          required
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label className="rs-check">
        <input
          type="checkbox"
          checked={allowRangeChange}
          onChange={(event) => setAllowRangeChange(event.target.checked)}
        />
        Allow viewers to change the date range
      </label>
      <label className="rs-field">
        Expires on (optional)
        <input
          type="date"
          value={expiresOn}
          onChange={(event) => setExpiresOn(event.target.value)}
        />
      </label>
      <label className="rs-field">
        Password (optional, at least {SHARE_PASSWORD_MIN} characters)
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          aria-invalid={tooShort}
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>
      <button type="submit" className="btn small" disabled={busy || !name.trim() || tooShort}>
        Create share link
      </button>
    </form>
  );
}

export function SharePanel({
  organisationId,
  documentId,
  documentName,
  range,
  onEditCopy,
  onClose,
}: {
  organisationId: string;
  documentId: string;
  documentName: string;
  range: RangeSetting;
  onEditCopy: (copyId: string) => void;
  onClose: () => void;
}): ReactNode {
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const reload = useCallback(
    () =>
      sharesApi.list(organisationId, documentId).then(setLinks, () => {
        setLinks([]);
        setProblem('Share links could not be loaded.');
      }),
    [organisationId, documentId],
  );
  useEffect(() => {
    void reload();
  }, [reload]);

  const act = (work: Promise<unknown>) =>
    work.then(
      () => {
        setProblem(null);
        return reload();
      },
      (error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'That did not work. Try again.'),
    );
  const copy = (link: ShareLink) =>
    navigator.clipboard.writeText(link.url).then(
      () => setCopied(link.id),
      () => setProblem('Copy did not work. Select the link and copy it yourself.'),
    );

  return (
    <aside className="rs" aria-label="Share link">
      <header className="rs-head">
        <strong>Share link</strong>
        <button type="button" className="rb-icon" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="rs-body">
        <p className="rs-explain">
          Anyone with the link sees a read-only copy with live numbers, without signing in. Later
          changes here do not change a link: use Edit copy for that.
        </p>
        <NewLinkForm
          defaultName={`${documentName} link`}
          onCreate={({ name, allowRangeChange, expiresOn, password }) =>
            act(
              sharesApi.create(organisationId, documentId, {
                name,
                allow_range_change: allowRangeChange,
                range,
                ...(expiresOn
                  ? { expires_at: new Date(`${expiresOn}T23:59:59`).toISOString() }
                  : {}),
                ...(password ? { password } : {}),
              }),
            )
          }
        />
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        <h3 className="rs-subhead">Links</h3>
        {links?.length === 0 && <p className="rs-explain">No share links yet.</p>}
        <ul className="rcm-threads">
          {(links ?? []).map((link) => (
            <li key={link.id} className="rcm-thread">
              <strong>{link.name}</strong>
              <small>
                {status(link)} · {link.view_count} views
                {link.has_password ? ' · password' : ''}
                {link.allow_range_change ? ' · dates changeable' : ''}
              </small>
              {!link.revoked_at && (
                <>
                  <input
                    className="sp-url"
                    readOnly
                    value={link.url}
                    aria-label={`Link for ${link.name}`}
                    onFocus={(event) => event.target.select()}
                  />
                  <div className="sp-actions">
                    <button type="button" className="btn small" onClick={() => void copy(link)}>
                      {copied === link.id ? 'Copied' : 'Copy link'}
                    </button>
                    <button
                      type="button"
                      className="btn small secondary"
                      onClick={() => onEditCopy(link.document_id)}
                    >
                      Edit copy
                    </button>
                    <button
                      type="button"
                      className="btn small secondary"
                      onClick={() => {
                        if (
                          !window.confirm(
                            `Revoke “${link.name}”? The link stops working for everyone.`,
                          )
                        )
                          return;
                        void act(sharesApi.revoke(organisationId, link.id));
                      }}
                    >
                      Revoke
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
